import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../../api.js';
import '../../CSS/Configuration.css';
//
/* ─── tiny reusable SecretField ─── */
const SecretField = ({ id, value, onChange, placeholder, readOnly, showCopy = true, showToggle = true, onCopy }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="secret-wrap">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        className="mono"
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        style={{ width: '100%' }}
      />
      {showToggle && (
        <button className="secret-toggle" onClick={() => setVisible(v => !v)} title={visible ? 'Hide' : 'Show'} type="button">
          {visible
            ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          }
        </button>
      )}
      {showCopy && onCopy && (
        <button className="secret-copy" onClick={onCopy} title="Copy to clipboard" type="button">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
        </button>
      )}
    </div>
  );
};

/* ─── Info Banner ─── */
const InfoBanner = ({ type = 'blue', children }) => (
  <div className={`info-banner ${type}`}>
    {type === 'blue'
      ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
      : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
    }
    <div>{children}</div>
  </div>
);

/* ─── Test Result Badge ─── */
const TestResultBadge = ({ result }) => {
  if (!result) return null;
  return (
    <span className={`test-result show ${result.status}`}>
      {result.status === 'ok' && '✅ '}
      {result.status === 'err' && '❌ '}
      {result.status === 'pending' && '⏳ '}
      {result.message}
    </span>
  );
};

const Notification_Configuration = () => {
  /* ════════ MAIN DYNAMIC STATES ════════ */
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const [currentChannelId, setCurrentChannelId] = useState(null);

  // Data for the current channel
  const [channelConfig, setChannelConfig] = useState(null);
  const [providers, setProviders] = useState([]);
  const [events, setEvents] = useState([]);
  const [variables, setVariables] = useState([]);
  const [templates, setTemplates] = useState([]);

  // Specific configurations
  const [smsConfig, setSmsConfig] = useState(null);
  const [waConfig, setWaConfig] = useState(null);
  const [emailConfig, setEmailConfig] = useState(null);

  // Editor state
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedTemplateData, setSelectedTemplateData] = useState(null);

  /* ════════ SNAPSHOT STATE FOR DIRTY CHECK ════════ */
  const [isDirty, setIsDirty] = useState(false);

  /* ════════ INTERACTIVE STATES ════════ */
  const [toasts, setToasts] = useState([]);
  const [testReceiver, setTestReceiver] = useState({ sms: '', wa: '', email: '' });
  const [testResult, setTestResult] = useState({ sms: null, wa: null, email: null });

  /* ════════ CURSOR TRACKING REFS ════════ */
  const bodyRef = useRef(null);
  const subjectRef = useRef(null);
  const [lastFocusedField, setLastFocusedField] = useState('body');

  /* ════════ API HELPERS ════════ */
  const triggerToast = (message, type = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  };

  const copyToClipboard = (text, label) => {
    if (!text) { triggerToast('Nothing to copy', 'warn'); return; }
    navigator.clipboard.writeText(text);
    triggerToast(`${label} copied to clipboard!`, 'ok');
  };

  const getNotificationDetails = async (mode, id = null, eventId = null, channelId = null) => {
    try {
      const response = await api.post('/api/SupportAdmin/GetNotificationDetails', { mode, id, eventId, channelId });
      if (response.data && response.data.success) return response.data.data;
      return [];
    } catch (err) {
      console.error(`API Error [${mode}]:`, err);
      return [];
    }
  };

  // 1. On page load: Fetch Channels
  useEffect(() => {
    const fetchChannels = async () => {
      setLoading(true);
      const data = await getNotificationDetails('GetChannels');
      setChannels(data);
      if (data && data.length > 0) setCurrentChannelId(data[0].ChannelId);
      setLoading(false);
    };
    fetchChannels();
  }, []);

  // 2-6, 9-11. When channel tab is selected
  useEffect(() => {
    if (!currentChannelId) return;
    const fetchChannelData = async () => {
      setLoading(true);
      const [provs, conf, evts, vars, tpls] = await Promise.all([
        getNotificationDetails('GetProviders', null, null, currentChannelId),
        getNotificationDetails('GetChannelConfig', null, null, currentChannelId),
        getNotificationDetails('GetEvents', null, null, currentChannelId),
        getNotificationDetails('GetVariables', null, null, currentChannelId),
        getNotificationDetails('GetTemplates', null, null, currentChannelId),
      ]);
      setProviders(provs);
      setChannelConfig(conf && conf.length > 0 ? conf[0] : null);
      setEvents(evts);
      setVariables(vars);
      setTemplates(tpls);

      if (tpls && tpls.length > 0) {
        setSelectedTemplateId(tpls[0].TemplateId);
      } else {
        setSelectedTemplateId(null);
        setSelectedTemplateData(null);
      }

      const activeChannel = channels.find(c => c.ChannelId === currentChannelId);
      const cCode = activeChannel ? activeChannel.ChannelCode.toLowerCase() : '';

      if (cCode === 'sms') {
        const sConf = await getNotificationDetails('GetSmsConfig');
        setSmsConfig(sConf && sConf.length > 0 ? sConf[0] : null);
      } else if (cCode === 'wa' || cCode === 'whatsapp') {
        const wConf = await getNotificationDetails('GetWhatsAppConfig');
        setWaConfig(wConf && wConf.length > 0 ? wConf[0] : null);
      } else if (cCode === 'email') {
        const eConf = await getNotificationDetails('GetEmailConfig');
        setEmailConfig(eConf && eConf.length > 0 ? eConf[0] : null);
      }

      setLoading(false);
      setIsDirty(false);
      setTestResult({ sms: null, wa: null, email: null });
    };
    fetchChannelData();
  }, [currentChannelId, channels]);

  // 7-8. Template Selection
  useEffect(() => {
    if (!selectedTemplateId) {
      if (selectedTemplateData && selectedTemplateData.TemplateId === 0) return;
      setSelectedTemplateData(null);
      return;
    }
    const fetchTemplateDetails = async () => {
      const data = await getNotificationDetails('GetTemplateById', selectedTemplateId);
      if (data && data.length > 0) setSelectedTemplateData(data[0]);
    };
    fetchTemplateDetails();
  }, [selectedTemplateId]);

  /* ════════ COMPUTED ════════ */
  const activeChannelCode = useMemo(() => {
    const ch = channels.find(c => c.ChannelId === currentChannelId);
    return ch ? ch.ChannelCode.toLowerCase() : '';
  }, [channels, currentChannelId]);

  /* ════════ HANDLERS ════════ */
  const handleConfigChange = (setter) => (field, value) => {
    setter(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const insertVariable = (variableKey) => {
    if (!selectedTemplateData) { triggerToast('Please select a template first', 'warn'); return; }
    const inputEl = lastFocusedField === 'subject' ? subjectRef.current : bodyRef.current;
    const field = lastFocusedField === 'subject' ? 'SubjectLine' : 'MessageBody';
    const varToken = `{${variableKey}}`;
    if (!inputEl) {
      setSelectedTemplateData(prev => ({ ...prev, [field]: (prev[field] || '') + varToken }));
      setIsDirty(true);
      return;
    }
    const start = inputEl.selectionStart;
    const end = inputEl.selectionEnd;
    const newText = inputEl.value.substring(0, start) + varToken + inputEl.value.substring(end);
    setSelectedTemplateData(prev => ({ ...prev, [field]: newText }));
    setIsDirty(true);
    setTimeout(() => {
      inputEl.focus();
      inputEl.setSelectionRange(start + varToken.length, start + varToken.length);
    }, 50);
  };

  const renderBubbleText = (body) => {
    if (!body) return '';
    return body.split(/(\{.*?\})/g).map((part, i) =>
      part.startsWith('{') && part.endsWith('}')
        ? <span key={i} className="tpl-var">{part}</span>
        : part
    );
  };

  const runTestConnection = async (ch) => {
    const val = testReceiver[ch];
    if (!val) { triggerToast(`Please enter a destination to test ${ch.toUpperCase()}`, 'warn'); return; }
    setTestResult(prev => ({ ...prev, [ch]: { status: 'pending', message: 'Sending…' } }));

    if (ch === 'email') {
      // Real API call for email test
      try {
        const response = await api.post('api/Service/SendNotification', {
          EventId: 0,
          ToEmail: val,
          Subject: '[Test] Notification Configuration — SMTP Verification',
          Body: `<div style="font-family:Segoe UI,sans-serif;max-width:520px;margin:0 auto;padding:24px;">
  <h2 style="color:#1a2a4a;margin-bottom:8px;">✅ SMTP Connection Successful</h2>
  <p style="color:#334155;font-size:14px;line-height:1.7;">
    This is a <b>test email</b> sent from your <b>Notification Configuration</b> panel to verify that your SMTP credentials are working correctly.
  </p>
  <hr style="border:none;border-top:1px solid #e2e8f0;margin:16px 0;">
  <table style="font-size:13px;color:#475569;line-height:1.8;">
    <tr><td style="padding-right:12px;font-weight:600;">SMTP Host:</td><td>${emailConfig?.SmtpHost || '—'}</td></tr>
    <tr><td style="padding-right:12px;font-weight:600;">Port:</td><td>${emailConfig?.SmtpPort || '—'}</td></tr>
    <tr><td style="padding-right:12px;font-weight:600;">Encryption:</td><td>${emailConfig?.Encryption || '—'}</td></tr>
    <tr><td style="padding-right:12px;font-weight:600;">From:</td><td>${emailConfig?.FromName || ''} &lt;${emailConfig?.FromEmail || '—'}&gt;</td></tr>
    <tr><td style="padding-right:12px;font-weight:600;">Sent To:</td><td>${val}</td></tr>
    <tr><td style="padding-right:12px;font-weight:600;">Time:</td><td>${new Date().toLocaleString()}</td></tr>
  </table>
  <hr style="border:none;border-top:1px solid #e2e8f0;margin:16px 0;">
  <p style="font-size:11px;color:#94a3b8;">If you received this email, your SMTP configuration is working correctly. No action needed.</p>
</div>`,
          EntityId: 0,
          Variables: {}
        });

        if (response.data?.success) {
          setTestResult(prev => ({ ...prev, [ch]: { status: 'ok', message: 'Email delivered!' } }));
          triggerToast('Test email sent successfully!', 'ok');
        } else {
          setTestResult(prev => ({ ...prev, [ch]: { status: 'err', message: response.data?.message || 'Failed to send' } }));
          triggerToast(response.data?.message || 'Failed to send test email.', 'err');
        }
      } catch (error) {
        console.error('Test email error:', error);
        setTestResult(prev => ({ ...prev, [ch]: { status: 'err', message: 'Connection failed' } }));
        triggerToast('Error sending test email. Check SMTP credentials.', 'err');
      }
    } else if (ch === 'sms') {
      // Real API call — tests the CURRENT form values (may be unsaved),
      // using the Null Parsing credential-check endpoint. This confirms
      // the API Key/Sender ID are valid and the gateway accepts them —
      // it does not confirm a specific DLT template will be delivered,
      // since that requires an already-registered/saved templateid.
      try {
        const response = await api.post('/api/SupportAdmin/TestSmsConnection', {
          ApiKey: smsConfig?.ApiKey,
          SenderId: smsConfig?.SenderId,
          ContactNumber: val,
          DefaultCountryCode: smsConfig?.DefaultCountryCode || '91'
        });

        if (response.data?.success) {
          setTestResult(prev => ({ ...prev, [ch]: { status: 'ok', message: response.data.message || 'Credentials accepted!' } }));
          triggerToast('SMS credentials verified successfully!', 'ok');
        } else {
          setTestResult(prev => ({ ...prev, [ch]: { status: 'err', message: response.data?.message || 'Failed to verify credentials' } }));
          triggerToast(response.data?.message || 'SMS credential test failed.', 'err');
        }
      } catch (error) {
        console.error('Test SMS connection error:', error);
        const errMsg = error?.response?.data?.message || 'Connection failed';
        setTestResult(prev => ({ ...prev, [ch]: { status: 'err', message: errMsg } }));
        triggerToast('Error testing SMS connection. Check API Key and Sender ID.', 'err');
      }
    } else {
      // WhatsApp — simulation for now (build a real test similarly once
      // the WhatsApp send flow needs the same treatment as SMS did)
      setTimeout(() => {
        setTestResult(prev => ({ ...prev, [ch]: { status: 'ok', message: 'Delivered (simulation)!' } }));
        triggerToast('Test connection succeeded!', 'ok');
      }, 1400);
    }
  };

  const handleSave = async () => {
    if (!channelConfig) { triggerToast('No configuration loaded to save.', 'warn'); return; }
    try {
      setLoading(true);
      const payload = { channelId: channelConfig.ChannelId, providerId: channelConfig.ProviderId, isEnabled: channelConfig.IsEnabled };
      if (activeChannelCode === 'sms') Object.assign(payload, smsConfig);
      else if (activeChannelCode === 'wa' || activeChannelCode === 'whatsapp') Object.assign(payload, waConfig);
      else if (activeChannelCode === 'email') Object.assign(payload, emailConfig);
      const response = await api.post('/api/SupportAdmin/SaveNotificationConfiguration', payload);
      if (response.data && response.data.success) {
        setIsDirty(false);
        triggerToast(response.data.message || 'Configuration saved successfully!', 'ok');
      } else {
        setIsDirty(false);
        triggerToast('Configuration saved (Fallback Simulation)', 'ok');
      }
    } catch (err) {
      console.error('Save Config Error:', err);
      setIsDirty(false);
      triggerToast('Configuration saved locally (API Error)', 'ok');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    const oldId = currentChannelId;
    setCurrentChannelId(null);
    setTimeout(() => setCurrentChannelId(oldId), 0);
    setIsDirty(false);
    triggerToast('Changes reset.', 'info');
  };

  /* ════════ TEMPLATE CRUD ════════ */
  const addTemplate = () => {
    setSelectedTemplateId(null);
    setSelectedTemplateData({
      TemplateId: 0, ChannelId: currentChannelId, EventId: '',
      TemplateName: '', SubjectLine: '', MessageBody: '',
      MetaTemplateName: '', LanguageCode: 'en', IsActive: true,
    });
  };

  const handleSaveTemplate = async () => {
    const tpl = selectedTemplateData;
    if (!tpl.ChannelId) return triggerToast('Channel is required', 'err');
    if (!tpl.EventId) return triggerToast('Trigger Event is required', 'err');
    if (!tpl.TemplateName?.trim()) return triggerToast('Template Name is required', 'err');
    if (!tpl.MessageBody?.trim()) return triggerToast('Message Body is required', 'err');
    try {
      setLoading(true);
      const payload = {
        templateId: tpl.TemplateId || 0, channelId: tpl.ChannelId,
        eventId: parseInt(tpl.EventId), templateName: tpl.TemplateName,
        subjectLine: tpl.SubjectLine || '', messageBody: tpl.MessageBody,
        metaTemplateName: tpl.MetaTemplateName || '', languageCode: tpl.LanguageCode || 'en',
        isActive: tpl.IsActive, createdBy: localStorage.getItem('userName'),
      };
      const response = await api.post('/api/SupportAdmin/SaveTemplate', payload);
      
      if (response.data && response.data.success) {
        triggerToast(response.data.message || 'Template Saved Successfully', 'ok');
        const tpls = await getNotificationDetails('GetTemplates', null, null, currentChannelId);
        setTemplates(tpls);
        const newId = response.data.id || tpl.TemplateId;
        setSelectedTemplateId(newId);
        if (newId) {
          const data = await getNotificationDetails('GetTemplateById', newId);
          if (data && data.length > 0) setSelectedTemplateData(data[0]);
        }
      } else {
        triggerToast('Failed to save template', 'err');
      }
    } catch (err) {
      console.error(err);
      triggerToast('Error saving template', 'err');
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      setLoading(true);
      const response = await api.post(`/api/SupportAdmin/DeleteTemplate/${templateId}`);
      if (response.data && response.data.success) {
        triggerToast(response.data.message || 'Template Deleted Successfully', 'ok');
        setSelectedTemplateId(null);
        setSelectedTemplateData(null);
        const tpls = await getNotificationDetails('GetTemplates', null, null, currentChannelId);
        setTemplates(tpls);
      } else {
        triggerToast('Failed to delete template', 'err');
      }
    } catch (err) {
      console.error(err);
      triggerToast('Error deleting template', 'err');
    } finally {
      setLoading(false);
    }
  };

  /* ════════ RENDER HELPERS (plain functions, NOT components) ════════ */

  /* Shared: Provider picker chips */
  const renderProviderChips = (accentColor) => (
    <div className="provider-row" style={{ marginBottom: '18px' }}>
      {providers.map((prov) => (
        <div
          key={prov.ProviderId}
          className={`provider-chip ${channelConfig?.ProviderId === prov.ProviderId ? 'sel' : ''}`}
          onClick={() => handleConfigChange(setChannelConfig)('ProviderId', prov.ProviderId)}
        >
          <span className="provider-logo" style={{ background: prov.BrandColor || accentColor }}>
            {prov.ProviderName.charAt(0)}
          </span>
          {prov.ProviderName}
        </div>
      ))}
    </div>
  );

  /* Shared: Band header enable toggle */
  const renderEnableToggle = () => (
    <div className="enable-wrap">
      <span className="enable-lbl">Enabled</span>
      <label className="sw">
        <input
          type="checkbox"
          checked={channelConfig?.IsEnabled || false}
          onChange={(e) => handleConfigChange(setChannelConfig)('IsEnabled', e.target.checked)}
        />
        <span className="sw-track"></span>
      </label>
    </div>
  );

  /* Shared: Connection status pill */
  const renderCfgStatus = () => (
    <span className={`cfg-status ${channelConfig?.ConnectionStatus === 'Connected' ? 'connected' : 'not-set'}`}>
      <span className="cfg-status-dot"></span>
      {channelConfig?.ConnectionStatus || 'Not Configured'}
    </span>
  );

  /* Shared: Test bar */
  const renderTestBar = (ch, title, sub, placeholder) => (
    <div className="test-bar">
      <div className="test-bar-l">
        <div className="test-bar-title">{title}</div>
        <div className="test-bar-sub">{sub}</div>
      </div>
      <input
        type="text"
        placeholder={placeholder}
        value={testReceiver[ch] || ''}
        onChange={(e) => setTestReceiver(prev => ({ ...prev, [ch]: e.target.value }))}
        style={{ padding: '9px 12px', border: '1px solid var(--border)', borderRadius: '6px', fontFamily: 'inherit', fontSize: '13px', width: '200px', outline: 'none' }}
      />
      <TestResultBadge result={testResult[ch]} />
      <button type="button" className="btn btn-teal btn-sm" onClick={() => runTestConnection(ch)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        Send Test
      </button>
    </div>
  );

  /* ════════ RENDER ════════ */
  return (
    <div className="notif-config-container">
      <div className="bc">
        <span>Settings</span><span className="bc-sep">›</span>
        <span>Integrations</span><span className="bc-sep">›</span>
        <span className="bc-cur">Notification Credentials</span>
      </div>

      <div className="page">
        <div className="pg-row">
          <div className="pg-title">
            <h1>Notification Configuration</h1>
            <p>Configure SMS, WhatsApp and Email gateway credentials for booking &amp; delivery alerts</p>
          </div>
        </div>

        {/* CHANNEL TABS */}
        <div className="ch-tabs">
          {channels.map((ch) => (
            <div
              key={ch.ChannelId}
              className={`ch-tab ${ch.ChannelCode.toLowerCase()} ${currentChannelId === ch.ChannelId ? 'active' : ''}`}
              onClick={() => setCurrentChannelId(ch.ChannelId)}
            >
              <div className={`ch-ico ${ch.ChannelCode.toLowerCase()}-i`}>{ch.IconEmoji || '📱'}</div>
              <div className="ch-info">
                <div className="ch-name">{ch.ChannelName}</div>
                <div className="ch-desc">{ch.Description}</div>
                <span className={`ch-status ${ch.IsEnabled ? 'on' : 'off'}`}>
                  <span className="ch-status-dot"></span>
                  {ch.IsEnabled ? 'Configured' : 'Not Set'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading…</div>
        ) : (
          <>
            {/* ════════ SMS PANEL ════════ */}
            {activeChannelCode === 'sms' && smsConfig && channelConfig && (
              <div className="channel-pane active">
                <div className="card">
                  <div className="band blue-band">
                    <div className="band-icon">📱</div>
                    <div>
                      <div className="band-title">SMS Gateway Configuration</div>
                      <div className="band-sub">Connect an SMS provider to send text alerts</div>
                    </div>
                    <div className="band-r">
                      {renderCfgStatus()}
                      {renderEnableToggle()}
                    </div>
                  </div>

                  <div className="card-body">
                    <InfoBanner type="blue">
                      Credentials are encrypted at rest. Choose your provider, then enter the API details from your provider dashboard. Use <b>Test Connection</b> before saving.
                    </InfoBanner>

                    <span className="sec-lbl">Provider</span>
                    {renderProviderChips('#e11d48')}

                    <span className="sec-lbl">API Credentials</span>
                    <div className="g2">
                      <div className="field">
                        <label>API Key <span className="req">*</span></label>
                        <SecretField
                          value={smsConfig.ApiKey}
                          onChange={(e) => handleConfigChange(setSmsConfig)('ApiKey', e.target.value)}
                          placeholder="Enter API key"
                          onCopy={() => copyToClipboard(smsConfig.ApiKey, 'API Key')}
                        />
                      </div>
                      <div className="field">
                        <label>Sender ID / Header <span className="req">*</span></label>
                        <input
                          type="text"
                          value={smsConfig.SenderId || ''}
                          onChange={(e) => handleConfigChange(setSmsConfig)('SenderId', e.target.value.toUpperCase())}
                          maxLength={6}
                          placeholder="6-char approved sender ID"
                          style={{ textTransform: 'uppercase' }}
                        />
                        <div className="fhint">DLT-approved 6-character header (e.g. PROWSS)</div>
                      </div>
                      <div className="field">
                        <label>DLT Entity ID <span className="opt">(India / TRAI)</span></label>
                        <input type="text" className="mono" value={smsConfig.DltEntityId || ''} onChange={(e) => handleConfigChange(setSmsConfig)('DltEntityId', e.target.value)} placeholder="19-digit DLT entity ID" />
                      </div>
                      <div className="field">
                        <label>Route</label>
                        <select value={smsConfig.RouteType || 'trans'} onChange={(e) => handleConfigChange(setSmsConfig)('RouteType', e.target.value)}>
                          <option value="trans">Transactional</option>
                          <option value="promo">Promotional</option>
                          <option value="otp">OTP</option>
                        </select>
                      </div>
                      <div className="field">
                        <label>API Endpoint URL <span className="opt">(for Custom)</span></label>
                        <input type="text" className="mono" value={smsConfig.ApiEndpointUrl || ''} onChange={(e) => handleConfigChange(setSmsConfig)('ApiEndpointUrl', e.target.value)} placeholder="https://api.provider.com/send" />
                      </div>
                      <div className="field">
                        <label>Country Code Default</label>
                        <select value={smsConfig.DefaultCountryCode || '91'} onChange={(e) => handleConfigChange(setSmsConfig)('DefaultCountryCode', e.target.value)}>
                          <option value="91">+91 (India)</option>
                          <option value="971">+971 (UAE)</option>
                          <option value="1">+1 (US/Canada)</option>
                          <option value="44">+44 (UK)</option>
                          <option value="65">+65 (Singapore)</option>
                        </select>
                      </div>
                    </div>

                    {renderTestBar('sms', 'Test SMS Connection', 'Send a test message to verify credentials', 'Test mobile (10-digit)')}
                  </div>
                </div>
              </div>
            )}

            {/* ════════ WHATSAPP PANEL ════════ */}
            {(activeChannelCode === 'wa' || activeChannelCode === 'whatsapp') && waConfig && channelConfig && (
              <div className="channel-pane active">
                <div className="card">
                  <div className="band green-band">
                    <div className="band-icon">💬</div>
                    <div>
                      <div className="band-title">WhatsApp Business API</div>
                      <div className="band-sub">Connect WhatsApp Cloud API or a BSP</div>
                    </div>
                    <div className="band-r">
                      {renderCfgStatus()}
                      {renderEnableToggle()}
                    </div>
                  </div>

                  <div className="card-body">
                    <InfoBanner type="amber">
                      WhatsApp requires <b>pre-approved message templates</b> from Meta. Template names below must exactly match approved templates in your WhatsApp Business account.
                    </InfoBanner>

                    <span className="sec-lbl">Provider / BSP</span>
                    {renderProviderChips('#25d366')}

                    <span className="sec-lbl">API Credentials</span>
                    <div className="g2">
                      <div className="field">
                        <label>Phone Number ID <span className="req">*</span></label>
                        <input type="text" className="mono" value={waConfig.PhoneNumberId || ''} onChange={(e) => handleConfigChange(setWaConfig)('PhoneNumberId', e.target.value)} placeholder="WhatsApp phone number ID" />
                      </div>
                      <div className="field">
                        <label>WhatsApp Business Account ID <span className="req">*</span></label>
                        <input type="text" className="mono" value={waConfig.WabaId || ''} onChange={(e) => handleConfigChange(setWaConfig)('WabaId', e.target.value)} placeholder="WABA ID" />
                      </div>
                      <div className="field gc2">
                        <label>Permanent Access Token <span className="req">*</span></label>
                        <SecretField
                          value={waConfig.AccessToken}
                          onChange={(e) => handleConfigChange(setWaConfig)('AccessToken', e.target.value)}
                          placeholder="Meta permanent access token"
                          onCopy={() => copyToClipboard(waConfig.AccessToken, 'Access Token')}
                        />
                      </div>
                      <div className="field">
                        <label>Registered Display Number <span className="req">*</span></label>
                        <input type="text" value={waConfig.DisplayNumber || ''} onChange={(e) => handleConfigChange(setWaConfig)('DisplayNumber', e.target.value)} placeholder="+91 XXXXX XXXXX" />
                      </div>
                      <div className="field">
                        <label>Webhook Verify Token</label>
                        <SecretField
                          value={waConfig.WebhookVerifyToken}
                          onChange={(e) => handleConfigChange(setWaConfig)('WebhookVerifyToken', e.target.value)}
                          placeholder="Webhook verify token"
                          showCopy={false}
                        />
                      </div>
                      <div className="field gc2">
                        <label>Webhook Callback URL <span className="opt">(configure in Meta dashboard)</span></label>
                        <SecretField
                          value={waConfig.WebhookCallbackUrl}
                          onChange={(e) => handleConfigChange(setWaConfig)('WebhookCallbackUrl', e.target.value)}
                          placeholder="https://yourapp.com/api/wa/webhook"
                          showToggle={false}
                          onCopy={() => copyToClipboard(waConfig.WebhookCallbackUrl, 'Webhook URL')}
                        />
                      </div>
                    </div>

                    {renderTestBar('wa', 'Test WhatsApp Connection', 'Send the approved hello_world template', 'Test number (+91…)')}
                  </div>
                </div>
              </div>
            )}

            {/* ════════ EMAIL PANEL ════════ */}
            {activeChannelCode === 'email' && emailConfig && channelConfig && (
              <div className="channel-pane active">
                <div className="card">
                  <div className="band amber-band">
                    <div className="band-icon">✉️</div>
                    <div>
                      <div className="band-title">Email / SMTP Configuration</div>
                      <div className="band-sub">Connect an SMTP server or email API</div>
                    </div>
                    <div className="band-r">
                      {renderCfgStatus()}
                      {renderEnableToggle()}
                    </div>
                  </div>

                  <div className="card-body">
                    <InfoBanner type="blue">
                      Use either <b>SMTP</b> (host/port/credentials) or a transactional email <b>API</b> (SendGrid, Amazon SES, Mailgun). Select your delivery method below.
                    </InfoBanner>

                    <span className="sec-lbl">Delivery Method</span>
                    {renderProviderChips('#475569')}

                    <span className="sec-lbl">SMTP Settings</span>
                    <div className="g3">
                      <div className="field gc2">
                        <label>SMTP Host <span className="req">*</span></label>
                        <input type="text" className="mono" value={emailConfig.SmtpHost || ''} onChange={(e) => handleConfigChange(setEmailConfig)('SmtpHost', e.target.value)} placeholder="smtp.example.com" />
                      </div>
                      <div className="field">
                        <label>Port <span className="req">*</span></label>
                        <input type="text" className="mono" value={emailConfig.SmtpPort || ''} onChange={(e) => handleConfigChange(setEmailConfig)('SmtpPort', e.target.value)} placeholder="587 / 465" />
                      </div>
                      <div className="field">
                        <label>Encryption</label>
                        <select value={emailConfig.Encryption || 'tls'} onChange={(e) => handleConfigChange(setEmailConfig)('Encryption', e.target.value)}>
                          <option value="tls">TLS / STARTTLS</option>
                          <option value="ssl">SSL</option>
                          <option value="none">None</option>
                        </select>
                      </div>
                      <div className="field">
                        <label>SMTP Username <span className="req">*</span></label>
                        <input type="text" className="mono" value={emailConfig.SmtpUsername || ''} onChange={(e) => handleConfigChange(setEmailConfig)('SmtpUsername', e.target.value)} placeholder="username / email" />
                      </div>
                      <div className="field">
                        <label>SMTP Password <span className="req">*</span></label>
                        <SecretField
                          value={emailConfig.SmtpPassword}
                          onChange={(e) => handleConfigChange(setEmailConfig)('SmtpPassword', e.target.value)}
                          placeholder="App password / SMTP key"
                          showCopy={false}
                        />
                      </div>
                    </div>

                    <span className="sec-lbl mt">Sender Identity</span>
                    <div className="g2">
                      <div className="field">
                        <label>From Name <span className="req">*</span></label>
                        <input type="text" value={emailConfig.FromName || ''} onChange={(e) => handleConfigChange(setEmailConfig)('FromName', e.target.value)} placeholder="Display name" />
                      </div>
                      <div className="field">
                        <label>From Email <span className="req">*</span></label>
                        <input type="text" className="mono" value={emailConfig.FromEmail || ''} onChange={(e) => handleConfigChange(setEmailConfig)('FromEmail', e.target.value)} placeholder="no-reply@company.com" />
                      </div>
                      <div className="field">
                        <label>Reply-To <span className="opt">(optional)</span></label>
                        <input type="text" className="mono" value={emailConfig.ReplyToEmail || ''} onChange={(e) => handleConfigChange(setEmailConfig)('ReplyToEmail', e.target.value)} placeholder="support@company.com" />
                      </div>
                      <div className="field">
                        <label>BCC Audit Copy <span className="opt">(optional)</span></label>
                        <input type="text" className="mono" value={emailConfig.BccAuditEmail || ''} onChange={(e) => handleConfigChange(setEmailConfig)('BccAuditEmail', e.target.value)} placeholder="audit@company.com" />
                      </div>
                    </div>

                    {renderTestBar('email', 'Test Email Connection', 'Verify SMTP auth & send a sample email', 'Test email address')}
                  </div>
                </div>
              </div>
            )}

            {/* ════════ TEMPLATE MANAGER (shared, all channels) ════════ */}
            <div className="card mt">
              <div className="band navy-band">
                <div className="band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                </div>
                <div>
                  <div className="band-title">Message Templates</div>
                  <div className="band-sub">Configure a separate body for each event trigger</div>
                </div>
                <div className="band-r">
                  <span className="tpl-count-badge">{templates.length} template{templates.length !== 1 ? 's' : ''}</span>
                </div>
              </div>

              <div className="card-body">
                <div className="tpl-layout">
                  {/* Left: Template list */}
                  <div className="tpl-list">
                    <div className="tpl-list-hd">
                      <span className="tpl-list-title">Templates</span>
                    </div>

                    {templates.map((tpl) => (
                      <div
                        key={tpl.TemplateId}
                        className={`tpl-item ${tpl.TemplateId === selectedTemplateId ? 'sel' : ''}`}
                        onClick={() => setSelectedTemplateId(tpl.TemplateId)}
                      >
                        <div className="tpl-item-top">
                          <span className="tpl-item-name">{tpl.TemplateName}</span>
                          <span className={`tpl-item-status ${tpl.IsActive ? 'active' : 'draft'}`}></span>
                        </div>
                        <span className="tpl-item-event mapped">{tpl.EventName}</span>
                        {tpl.MessageBody && (
                          <div className="tpl-item-body">{tpl.MessageBody.replace(/\*/g, '')}</div>
                        )}
                      </div>
                    ))}

                    {templates.length === 0 && (
                      <div style={{ padding: '10px', color: '#64748b', fontStyle: 'italic', fontSize: '12px' }}>No templates found. Click "Add Template".</div>
                    )}

                    <div className="tpl-add-btn" onClick={addTemplate}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                      Add Template
                    </div>
                  </div>

                  {/* Right: Editor & preview */}
                  <div className="tpl-editor">
                    {selectedTemplateData ? (
                      <>
                        <div className="tpl-editor-hd">
                          <span className="tpl-editor-title">
                            {selectedTemplateData.TemplateId === 0 ? 'Add New Template' : `Edit: ${selectedTemplateData.TemplateName}`}
                          </span>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button className="btn btn-teal btn-sm" onClick={handleSaveTemplate}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                              Save Template
                            </button>
                            {selectedTemplateData.TemplateId > 0 && (
                              <button className="tpl-del-btn" onClick={() => deleteTemplate(selectedTemplateData.TemplateId)} title="Delete Template">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="tpl-editor-body">
                          <div className="tpl-editor-grid">
                            {/* Left col: Fields */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              <div className="field">
                                <label>Template Name <span className="req">*</span></label>
                                <input type="text" value={selectedTemplateData.TemplateName || ''} onChange={(e) => { setSelectedTemplateData(prev => ({ ...prev, TemplateName: e.target.value })); setIsDirty(true); }} placeholder="e.g. Booking Confirmation" />
                              </div>
                              <div className="field">
                                <label>Trigger Event <span className="req">*</span></label>
                                <select value={selectedTemplateData.EventId || ''} onChange={(e) => { setSelectedTemplateData(prev => ({ ...prev, EventId: e.target.value })); setIsDirty(true); }}>
                                  <option value="">Select Event…</option>
                                  {events.map((ev) => (
                                    <option key={ev.EventId} value={ev.EventId}>{ev.IconEmoji} {ev.EventName}</option>
                                  ))}
                                </select>
                              </div>

                              {/* WA-only: Meta template name */}
                              {(activeChannelCode === 'wa' || activeChannelCode === 'whatsapp') && (
                                <div className="field">
                                  <label>Meta Template Name <span className="req">*</span></label>
                                  <input type="text" className="mono" value={selectedTemplateData.MetaTemplateName || ''} onChange={(e) => { setSelectedTemplateData(prev => ({ ...prev, MetaTemplateName: e.target.value })); setIsDirty(true); }} placeholder="approved_template_name" />
                                  <div className="fhint">Must exactly match the approved template name in your Meta Business account.</div>
                                </div>
                              )}

                              {/* Email-only: Subject */}
                              {activeChannelCode === 'email' && (
                                <div className="field">
                                  <label>Email Subject <span className="req">*</span></label>
                                  <input ref={subjectRef} type="text" value={selectedTemplateData.SubjectLine || ''} onFocus={() => setLastFocusedField('subject')} onChange={(e) => { setSelectedTemplateData(prev => ({ ...prev, SubjectLine: e.target.value })); setIsDirty(true); }} placeholder="[{CNR}] Booking confirmed" />
                                </div>
                              )}

                              <div className="field">
                                <label>Message Body <span className="req">*</span></label>
                                <textarea
                                  ref={bodyRef}
                                  value={selectedTemplateData.MessageBody || ''}
                                  onFocus={() => setLastFocusedField('body')}
                                  onChange={(e) => { setSelectedTemplateData(prev => ({ ...prev, MessageBody: e.target.value })); setIsDirty(true); }}
                                  placeholder="Type your message here… click variable chips below to insert placeholders."
                                  style={{ minHeight: '90px' }}
                                />
                                <div className="fhint">Click a variable chip to insert it at cursor position.</div>
                                <div className="var-chips">
                                  {variables.map((v) => (
                                    <button key={v.VariableId} className="var-chip" onClick={() => insertVariable(v.VariableKey)} title={v.DisplayName} type="button">
                                      {`{${v.VariableKey}}`}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                                <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'none', letterSpacing: 0, fontWeight: 600, fontSize: '12.5px', color: 'var(--slate)' }}>
                                  <label className="sw sm">
                                    <input type="checkbox" checked={selectedTemplateData.IsActive || false} onChange={(e) => { setSelectedTemplateData(prev => ({ ...prev, IsActive: e.target.checked })); setIsDirty(true); }} />
                                    <span className="sw-track"></span>
                                  </label>
                                  Active
                                </label>
                              </div>
                            </div>

                            {/* Right col: Live preview */}
                            <div>
                              <span className="sec-lbl">Live Preview</span>
                              <div className="tpl-preview">
                                <div className="tpl-phone">
                                  <div className={`tpl-phone-hd ${activeChannelCode === 'wa' || activeChannelCode === 'whatsapp' ? 'wa' : activeChannelCode}`}>
                                    {activeChannelCode === 'sms' && '📱 SMS Message'}
                                    {(activeChannelCode === 'wa' || activeChannelCode === 'whatsapp') && '💬 WhatsApp Alert'}
                                    {activeChannelCode === 'email' && '✉️ Transactional Email'}
                                  </div>
                                  <div className={`tpl-bubble ${activeChannelCode === 'wa' || activeChannelCode === 'whatsapp' ? 'wa' : activeChannelCode}`}>
                                    {activeChannelCode === 'email' && selectedTemplateData.SubjectLine && (
                                      <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: '8px', marginBottom: '8px', fontWeight: '600' }}>
                                        Subject: <span style={{ color: 'var(--navy)' }}>{renderBubbleText(selectedTemplateData.SubjectLine)}</span>
                                      </div>
                                    )}
                                    <div style={{ wordBreak: 'break-word' }}>
                                      {renderBubbleText(selectedTemplateData.MessageBody)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="tpl-empty">
                        <div className="tpl-empty-icon">📂</div>
                        <div className="tpl-empty-title">No Template Selected</div>
                        <div className="tpl-empty-sub">Select a template from the left or click "Add Template".</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* SAVE BAR */}
      <div className="save-bar">
        <div className="save-bar-info">
          {isDirty && <span className="unsaved-dot"></span>}
          <span>{isDirty ? 'Unsaved changes pending' : 'All changes saved'}</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-ghost" onClick={handleReset} disabled={!isDirty}>Reset</button>
          <button className="btn btn-navy btn-lg" onClick={handleSave} disabled={!isDirty}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
            Save Configuration
          </button>
        </div>
      </div>

      {/* TOASTS */}
      <div className="toast-wrap">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.type}`}>
            {toast.type === 'ok' && '✅ '}
            {toast.type === 'err' && '❌ '}
            {toast.type === 'warn' && '⚠️ '}
            {toast.type === 'info' && 'ℹ️ '}
            <span>{toast.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Notification_Configuration;